/**
 * Guest browsing — the single source of truth for "this visitor chose to look
 * around without an account".
 *
 * ## Why the /browse redirect is gone
 *
 * `Browse` used to bounce any signed-out visitor who had not completed
 * onboarding back to "/" with `replace: true`. That made sense when "/" WAS the
 * onboarding welcome screen. It is no longer true: `Index` renders the public
 * marketing homepage for signed-out visitors and only opens `OnboardingFlow`
 * for `?start=1` or for a signed-in account with onboarding incomplete.
 *
 * So for a signed-out visitor the bounce did not return them to onboarding, it
 * returned them to marketing — and the marketing homepage links to /browse in
 * thirteen places, so the two pages simply threw the visitor back and forth.
 * `/browse` is also listed in the sitemap at priority 0.9, which pointed every
 * crawler (always empty localStorage) at a redirect back to the homepage.
 *
 * The guest browse experience itself is fully built and unchanged — the guest
 * grade strip, the café detail lock, and the auth-gated filters all still
 * behave exactly as before. Only reachability changed.
 *
 * ## What this key is still for
 *
 * It records EXPLICIT intent: the visitor pressed something that said "let me
 * look around" rather than arriving by deep link or crawl. Nothing gates on it
 * today. It is kept because it is the signal any future re-tightening would
 * need, and because analytics wants to tell a chosen guest session apart from
 * an incidental one. Every entry point records it through `markGuestBrowse()`
 * so there is exactly one writer.
 *
 * The key deliberately carries the `grounded_browse_` prefix so the existing
 * sweep in `clearSignedOutLocalState()` (src/lib/authStore.ts) clears it on
 * sign-out. It is session-scoped intent, not a durable entitlement, and nothing
 * needs it to survive an account change.
 */

/** Prefixed so `clearSignedOutLocalState()` sweeps it on sign-out. */
export const GUEST_BROWSE_KEY = "grounded_browse_guest";

/**
 * Record that this visitor deliberately entered guest browsing.
 *
 * The ONE place this key is ever written. Called by the onboarding welcome
 * screen's "Just looking?" button and by every marketing-homepage link that
 * points at /browse (via `HomeLink`). Safe to call repeatedly.
 */
export function markGuestBrowse(): void {
  try {
    localStorage.setItem(GUEST_BROWSE_KEY, "true");
  } catch {
    /* private mode or storage full: intent is advisory, never block navigation */
  }
}

/** Whether this visitor previously chose guest browsing. */
export function hasGuestBrowseIntent(): boolean {
  try {
    return localStorage.getItem(GUEST_BROWSE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * The key this replaced. Kept as a name ONLY so the one-time purge below can
 * find it — nothing writes it, and nothing reads it for meaning.
 */
const LEGACY_GUEST_BROWSE_KEY = "grounded_guest_browsing";

/**
 * One-time cleanup for browsers carrying the pre-rename key.
 *
 * `clearSignedOutLocalState()` (src/lib/authStore.ts) sweeps `grounded_browse_`
 * keys on sign-out, and the legacy name does not carry that prefix — which is
 * exactly why it was renamed. But that left every browser already holding
 * `grounded_guest_browsing` stuck with it forever: nothing writes it, nothing
 * reads it, and no sweep can reach it. This removes it once, at startup.
 *
 * A remover, not a writer: it never sets guest-browse intent, so
 * `markGuestBrowse()` remains the single writer of that state.
 *
 * Safe on every boot — `removeItem` on an absent key is a no-op — so it needs
 * no "have I run yet" marker, which would only trade one orphan for another.
 */
export function purgeLegacyGuestBrowseKey(): void {
  try {
    localStorage.removeItem(LEGACY_GUEST_BROWSE_KEY);
  } catch {
    /* private mode or storage blocked: nothing reachable to clean up */
  }
}
